import {
  createContext,
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react'
import { useDispatch } from 'react-redux'
import { useParams, useHistory, useRouteMatch } from 'react-router-dom'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { Submission } from '@global/types'
import { reducer } from './reducer'
import { ActionType, ContextValues, Order, OrderBy, SortOptions, ContextState } from './types'
import { ALL_USERS, LIMIT_PER_PAGE, TODAY } from '../../config'
import { createSubmittedByOptions, getRequestsWithinDateRange } from '../../utils'
import { t } from '@lingui/core/macro'

const initialState: ContextState = {
  isLoading: true,
  page: 0,
  reviewingRequest: null,
  requestsBySubmitter: null,
  requestsWithinDateRange: [],
  submittedBy: ALL_USERS,
  submittedByOptions: [ALL_USERS],
  submittedFrom: null,
  submittedTo: TODAY,
  reloadApprovalRequests: false,
  sortOptions: {
    orderBy: OrderBy.CREATED_AT,
    order: Order.DESC,
    offset: 0,
  },
}

export const ApprovalContext = createContext<ContextValues | null>(null)

interface Props {
  approvalRequests: Submission[] | null
  fetchApprovalRequests: ({
    orderBy,
    order,
    offset,
  }: {
    orderBy: OrderBy
    order: Order
    offset: number
  }) => Promise<Submission[]>
  setApprovalRequestsCount: Dispatch<SetStateAction<number>>
  children: ReactElement | ReactElement[]
}

interface ApprovalContextProviderRouterParams {
  id?: string
  datasetId?: string
}

export const ApprovalContextProvider = ({
  approvalRequests,
  fetchApprovalRequests,
  setApprovalRequestsCount,
  children,
}: Props) => {
  const history = useHistory()
  const params = useParams<ApprovalContextProviderRouterParams>()
  const { url } = useRouteMatch()
  const reduxDispatch = useDispatch()
  const [state, dispatch] = useReducer(reducer, initialState)
  const { isLoading, sortOptions, submittedBy, submittedFrom, submittedTo } = state

  const filtersHaveChanged = useMemo(() => {
    const submittedByChanged = submittedBy.label !== initialState.submittedBy.label
    const submittedFromChanged = submittedFrom !== initialState.submittedFrom
    const submittedToChanged = submittedTo !== initialState.submittedTo

    return submittedByChanged || submittedFromChanged || submittedToChanged
  }, [submittedBy.label, submittedFrom, submittedTo])

  useEffect(() => {
    if (!approvalRequests) return

    const withinDateRange = getRequestsWithinDateRange({
      requests: approvalRequests,
      start: submittedFrom,
      end: submittedTo,
    })
    const submitters = createSubmittedByOptions(withinDateRange)

    dispatch({
      type: ActionType.setApprovalRequests,
      payload: {
        requestsWithinDateRange: withinDateRange,
        submittedByOptions: [ALL_USERS, ...submitters],
        isLoading: false,
        page: sortOptions.offset / LIMIT_PER_PAGE,
        sortOptions: { ...sortOptions },
      },
    })
  }, [approvalRequests])

  useEffect(() => {
    if (!approvalRequests) return
    if (!params.datasetId) return

    const matchingApprovalRequest = approvalRequests.find(
      approvalRequest => String(approvalRequest.dataset_id) === params.datasetId,
    )

    dispatch({
      type: ActionType.setReviewingRequest,
      payload: {
        reviewingRequest: matchingApprovalRequest,
      },
    })
  }, [approvalRequests])

  const value = {
    ...state,
    approvalRequests,
    filtersHaveChanged,
    dispatch,
    updateApprovalRequests,
    handleResetFilters,
    showSpinner,
    showApprovalRequestsTable,
    showReviewingApprovalRequest,
  }

  return <ApprovalContext.Provider value={value}>{children}</ApprovalContext.Provider>

  /**
   * Retrieves the updated list of submissions awaiting approval and updates the state
   *
   * @param {OrderBy} orderBy - The column to sort by
   * @param {Order} order - The order to sort by (asc or desc)
   * @param {number} offset - For pagination, offset is equal to the number of rows per page * the current page
   */
  async function updateApprovalRequests({
    orderBy = sortOptions.orderBy,
    order = sortOptions.order,
    offset = sortOptions.offset,
  }: SortOptions): Promise<any> {
    if (!isLoading) {
      dispatch({
        type: ActionType.setIsLoading,
        payload: true,
      })
    }

    try {
      const data = await fetchApprovalRequests({ orderBy, order, offset })
      const withinDateRange = getRequestsWithinDateRange({
        requests: data,
        start: submittedFrom,
        end: submittedTo,
      })

      const submitters = createSubmittedByOptions(withinDateRange)

      setApprovalRequestsCount(data.length)
      dispatch({
        type: ActionType.setApprovalRequests,
        payload: {
          requestsWithinDateRange: withinDateRange,
          submittedByOptions: [ALL_USERS, ...submitters],
          isLoading: false,
          page: offset / LIMIT_PER_PAGE,
          sortOptions: {
            order,
            orderBy,
            offset,
          },
        },
      })
    } catch (_error) {
      dispatch({
        type: ActionType.setApprovalRequests,
        payload: {
          requestsWithinDateRange: initialState.requestsWithinDateRange,
          submittedByOptions: initialState.submittedByOptions,
          isLoading: false,
          page: offset / LIMIT_PER_PAGE,
          sortOptions: {
            order,
            orderBy,
            offset,
          },
        },
      })
      reduxDispatch(
        showSnackbar({
          message: t`Failed to load approval requests`,
          type: 'ERROR',
        }),
      )
    }
  }

  /**
   * Resets all filters (Submitted By, Submitted From, Submitted To) and reverts related state values
   */
  function handleResetFilters() {
    showSpinner(() => {
      const withinDateRange = getRequestsWithinDateRange({
        requests: approvalRequests ?? [],
        start: initialState.submittedFrom,
        end: initialState.submittedTo,
      })

      const submitters = createSubmittedByOptions(withinDateRange)

      dispatch({
        type: ActionType.resetAllFilters,
        payload: {
          page: initialState.page,
          submittedBy: initialState.submittedBy,
          submittedFrom: initialState.submittedFrom,
          submittedTo: initialState.submittedTo,
          requestsBySubmitter: null,
          requestsWithinDateRange: withinDateRange,
          submittedByOptions: [ALL_USERS, ...submitters],
          isLoading: false,
        },
      })
    })
  }

  /**
   * When updating filters, sometimes the table rows update too quickly, and it's not obvious the table updated.
   * This gives feedback to the user to indicate the table rows are updating.
   *
   * @param cb - Callback function to execute before setting loading to false
   */
  function showSpinner(cb: () => void) {
    dispatch({
      type: ActionType.setIsLoading,
      payload: true,
    })

    return setTimeout(() => {
      cb()
      dispatch({
        type: ActionType.setIsLoading,
        payload: false,
      })
    }, 750)
  }

  function showApprovalRequestsTable() {
    const onTemplateDetailsView = history.location.pathname.includes('/template')
    const updatedUrl = onTemplateDetailsView ? `/template/${params.id}/approval` : '/codes/approval'
    history.push(updatedUrl)
  }

  function showReviewingApprovalRequest(datasetId) {
    history.push(`${url}/${datasetId}/review`)
  }
}

/**
 * Simplified hook to be used in components.
 * Throws and error if used incorrectly and ensures context is not null.
 *
 * @returns {ContextValues}
 */
export const useApprovalContext = () => {
  const context = useContext(ApprovalContext)

  if (!context) {
    throw new Error('useApprovalContext must be used within an ApprovalContextProvider')
  }

  return useContext(ApprovalContext) ?? null
}
